import {
  DatePickerUncontrolled,
  DateRangeSelect,
  Table,
} from "@4uhub/lib4uhub";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import MainApi from "../../../services/mainApi.service";
import { IActiveServiceSearch } from "../../../models/active-service-search";
import Ticket from "../EvaluationLogs/Ticket";
import { enUS, es, ptBR } from "date-fns/locale";
import { format } from "date-fns";
import EvaluationId from "../EvaluationLogs/EvaluationId";
import { useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import Form from "../EvaluationLogs/Form";

const ACTIVE_SERVICE_SEARCH_SURVEY_LOGS_ROUTE =
  process.env.REACT_APP_MAIN +
  "/api/v1/EvalMovEncSatisfaction/ActiveServiceSearch";
const activeServiceSearchSurveyLogsService = new MainApi<IActiveServiceSearch>(
  ACTIVE_SERVICE_SEARCH_SURVEY_LOGS_ROUTE
);

const translationPath = "page.evaluation.logs.";

const ActiveServiceSearchSurveyLogsPage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [enableFields, setEnableFields] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<Date | null>();

  const [startDate, setStartDate] = useState<Date | null>();

  const columns: GridColDef<IActiveServiceSearch>[] = useMemo(
    () => [
      {
        field: "evaluationForm",
        headerName: t(translationPath + "evaluationForm") || "Form",
        renderCell: (params) => {
          if (!params.value) return "-";
          return <Form data={params.value} />;
        },
      },
      {
        field: "externalUrl",
        headerName: t(translationPath + "externalUrl") || "Form",
        renderCell: (params) => {
          if (!params.value) return "-";
          return params.value;
        },
      },
      {
        field: "ticket",
        headerName: t(translationPath + "ticket") || "Ticket",
        renderCell: (params) => <Ticket data={params.value} />,
      },
      {
        field: "encounterIntegrationId",
        headerName:
          t(translationPath + "encounterIntegrationId") || "Encounter number",
      },
      {
        field: "created",
        headerName: t(translationPath + "shippingDate") || "Shipping Date",
        renderCell: (params) => {
          if (!params.value) return "-";

          const initialDate = new Date(params.value);
          return format(initialDate, "P pp", {
            locale:
              language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
          }).replaceAll(",", "");
        },
      },
      {
        field: "answerDate",
        headerName: t(translationPath + "answerDate") || "Answer Date",
        renderCell: (params) => {
          if (!params.value) return "-";

          const answerDate = new Date(params.value);
          return format(answerDate, "P pp", {
            locale:
              language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
          }).replaceAll(",", "");
        },
      },
      {
        field: "id",
        headerName: t(translationPath + "id") || "Id da Pesquisa",
        renderCell: (params) => (
          <EvaluationId
            data={params.value}
            portalDomain={params.row.portalDomain}
          />
        ),
      },
    ],
    [t, language]
  );

  const props = useMemo(() => {
    return {
      StartDate: startDate ? startDate : undefined,
      EndDate: endDate ? endDate : undefined,
    };
  }, [startDate, endDate]);

  return (
    <Table<IActiveServiceSearch>
      showDefaultMacroFilters={false}
      service={activeServiceSearchSurveyLogsService}
      columns={columns}
      title={t(translationPath + "title")}
      searchInput={false}
      showButton={false}
      showDelete={false}
      serviceProps={props}
      showDeleteButton={false}
      defaultActions={false}
      macroFilters={() => {
        return (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: "0 0 200px" }}>
              <DateRangeSelect
                setEndDate={setEndDate}
                setStartDate={setStartDate}
                setEnableFields={setEnableFields}
              />
            </Box>

            <Box sx={{ flex: "0 0 150px" }}>
              <DatePickerUncontrolled
                value={startDate}
                onChange={setStartDate}
                label={`${t(translationPath + "start_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
            <Box sx={{ flex: "0 0 150px" }}>
              <DatePickerUncontrolled
                value={endDate}
                onChange={setEndDate}
                label={`${t(translationPath + "end_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
          </Stack>
        );
      }}
      hideLog
      searchInputPlaceHolder={t(translationPath + "placeholder")}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default ActiveServiceSearchSurveyLogsPage;
